import TileLayer from '@arcgis/core/layers/TileLayer';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import GroupLayer from '@arcgis/core/layers/GroupLayer';
import FeatureEffect from '@arcgis/core/layers/support/FeatureEffect';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import MapView from '@arcgis/core/views/MapView';
import Layer from '@arcgis/core/layers/Layer';
import { ResourceHandle } from '@arcgis/core/core/Handles';

/**
 * Options for configuring a visual highlight effect controller that makes map features stand out against a dimmed background.
 *
 * @param view - The ArcGIS MapView instance used to access the map and listen for drag events.
 * @param featureLayer - The FeatureLayer whose features will be queried and highlighted based on the whereClause.
 * @param whereClause - A SQL-style where clause used to query which features from the FeatureLayer should be highlighted, e.g. 'OBJECTID IN (1,2,3)'
 * @param unHighlightOnMapMove - If true (the default), the highlight is automatically removed when the user drags the map, returning the view to normal. Set to false to keep the highlight active regardless of map movement.
 */
export interface HighlightEffectOptions {
  view: MapView;
  featureLayer: FeatureLayer;
  whereClause: string;
  /** Automatically deactivate the highlight when the user drags/moves the map. Defaults to true. */
  unHighlightOnMapMove?: boolean;
}

/**
 * An interface for controlling visual highlight effects on map graphics, making matching features bloom against a grayscale background and dimming everything else.
 * <para>Callers typically activate the effect when certain features are selected and deactivate or destroy it when they are deselected or the view is disposed.</para>
 */
export interface HighlightEffectController {
  activate: (options: HighlightEffectOptions) => Promise<void>;
  deactivate: (options: HighlightEffectOptions) => void;
  destroy: (view?: MapView) => void;
}

/**
 * Creates a highlight effect controller that makes FeatureLayer features matching a where clause visually stand out by blooming them against a grayscale background.
 *
 * Internally manages a GroupLayer mask built from the current basemap TileLayer, and a GraphicsLayer that clips to the feature geometry. Activation queries all matching features, applies a bloom/glow effect to them while graying out the rest of the basemap. Deactivation restores the basemap and leaves a subtle glow on matched features. Destroy fully tears down all layers and handles.
 *
 * @returns An HighlightEffectController with activate, deactivate, and destroy methods.
 */
export const createHighlightEffectController = (): HighlightEffectController => {
  let tileLayer: TileLayer | null = null;
  let graphicsLayer: GraphicsLayer | null = null;
  let groupLayer: GroupLayer | null = null;
  let dragHandle: ResourceHandle | null = null;

  /**
   * Derives a fresh TileLayer from the view's current basemap so the
   * highlight tile layer always matches when the basemap changes.
   */
  const buildTileLayerFromBasemap = (view: MapView): TileLayer => {
    const baseTileLayer = view.map?.basemap?.baseLayers?.find(
      (l: Layer) => l.type === 'tile'
    ) as TileLayer | undefined;

    const layer = new TileLayer({
      portalItem: baseTileLayer?.portalItem
        ? { id: baseTileLayer.portalItem.id }
        : { id: '10df2279f9684e4a9f6a7f08febac2a9' } // fallback: World Imagery
    });

    layer.when(() => {
      layer.sublayers?.forEach((sublayer) => { sublayer.popupEnabled = false; });
    });

    return layer;
  };

  /**
   * Rebuilds the GroupLayer from scratch each activate call (option 2) so
   * the internal TileLayer always reflects the current basemap.
   */
  const buildGroupLayer = (view: MapView): GroupLayer => {
    // Remove the old group layer from the map before rebuilding
    if (groupLayer) {
      view.map?.remove(groupLayer);
      groupLayer.destroy();
    }

    const stale = view.map?.findLayerById('highlight-effect-group');
    if (stale) {
      view.map?.remove(stale);
      stale.destroy();
    }
    tileLayer = buildTileLayerFromBasemap(view);

    graphicsLayer = new GraphicsLayer({
      blendMode: 'destination-in',
      title: 'highlight-clip-layer',
      listMode: 'hide'
    });

    groupLayer = new GroupLayer({
      id:'highlight-effect-group',
      title: 'Highlight Group Layer',
      layers: [tileLayer, graphicsLayer],
      opacity: 0,
      listMode: 'hide'
    });

    // Insert at bottom of operational layers so pins/boundaries render on top
    view.map?.add(groupLayer, 0);
    return groupLayer;
  };

  const activate = async ({ view, featureLayer, whereClause, unHighlightOnMapMove = true }: HighlightEffectOptions): Promise<void> => {
    const gl = buildGroupLayer(view);

    // Remove any previous drag listener before registering a new one
    dragHandle?.remove();
    dragHandle = null;

    // Query features matching the whereClause and copy geometry into graphicsLayer
    const featureSet = await featureLayer.queryFeatures({
      where: whereClause,
      returnGeometry: true,
      outFields: ['*'],
      cacheHint: true
    });

    graphicsLayer!.graphics.removeAll();
    graphicsLayer!.graphics.addMany(
      featureSet.features.map((f) => f.clone())
    );

    // Highlight the matching features, gray out the rest
    featureLayer.featureEffect = new FeatureEffect({
      filter: { where: whereClause },
      includedEffect: 'bloom(1, .1px, 0) opacity(1)',
      excludedEffect: 'grayscale(70%) opacity(0.5)'
    });

    // Activate the group layer mask
    gl.effect = 'brightness(1.4) drop-shadow(0, 0px, 12px)';
    gl.opacity = 1;

    // Grayscale the basemap base layers
    view.map?.basemap?.baseLayers?.forEach((l: Layer) => {
      (l as TileLayer).effect = 'grayscale(1)';
    });

    if (unHighlightOnMapMove) {
      dragHandle = view.on('drag', () => {
        deactivate({ view, featureLayer, whereClause });
        dragHandle?.remove();
        dragHandle = null;
      });
    }
  };

  const deactivate = ({ view, featureLayer, whereClause }: HighlightEffectOptions): void => {
    // Clean up drag listener if still attached
    dragHandle?.remove();
    dragHandle = null;

    if (groupLayer) {
      groupLayer.opacity = 0;
      groupLayer.effect = null;
    }

    // Restore basemap
    view.map?.basemap?.baseLayers?.forEach((l: Layer) => {
      (l as TileLayer).effect = null;
    });

    // Reset to a subtle glow — no excluded effect
    featureLayer.featureEffect = new FeatureEffect({
      filter: { where: whereClause },
      includedEffect: 'bloom(2, .01px, 0) opacity(.8)'
    });
  };

  const destroy = (view?: MapView): void => {
    dragHandle?.remove();
    dragHandle = null;

    if (groupLayer && view) {
      view.map?.remove(groupLayer);
      groupLayer.destroy();
    }
    tileLayer = null;
    graphicsLayer = null;
    groupLayer = null;
  };

  return { activate, deactivate, destroy };
};
